import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../utils/apiUtils";
import { clearCorporateSession } from "../utils/corporateSession";
import socketService from "../utils/socketService";
import {
  FaCalendarAlt,
  FaClock,
  FaCar,
  FaHourglassHalf,
  FaRoad,
  FaFilter,
  FaExclamationTriangle,
  FaWallet,
} from "react-icons/fa";
import {
  getPaymentMethodDisplay,
} from "../utils/paymentDisplayUtils";
import { convertTo12HourFormat } from "../utils/timeUtils";

const CorporateBookings = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchMe = async () => {
    try {
      const res = await api.get("/corporate/me");
      if (res?.data?.success) {
        const memberData = res.data.data;
        setMe(memberData);
        localStorage.setItem("corporateMember", JSON.stringify(memberData));
      } else {
        toast.error(res?.data?.message || "Failed to load profile");
        clearCorporateSession();
        navigate("/corporate/login", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load profile");
      clearCorporateSession();
      navigate("/corporate/login", { replace: true });
    }
  };

  const fetchWallet = async () => {
    try {
      const res = await api.get("/corporate/wallet");
      if (res?.data?.success) setWallet(res.data.data);
    } catch (err) {
      console.error("Failed to load wallet:", err.message);
    }
  };

  const fetchBookings = async () => {
    setListLoading(true);
    try {
      const url =
        statusFilter === "all"
          ? "/corporate/bookings"
          : `/corporate/bookings?status=${statusFilter}`;
      const res = await api.get(url);
      if (res?.data?.success) {
        setBookings(res.data.data || []);
      } else {
        toast.error(res?.data?.message || "Failed to load bookings");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load bookings");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchMe();
      await fetchWallet();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (me) fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?._id, statusFilter]);

  // Real-time socket updates for bookings
  useEffect(() => {
    if (!me) return;

    try {
      console.log("[Socket] Initializing connection for corporate member:", me._id);
      socketService.connect();

      const setupRooms = setInterval(() => {
        const isConnected = socketService.socket?.connected || socketService.isConnected;
        if (isConnected) {
          clearInterval(setupRooms);

          // Join corporate member room
          socketService.joinCorporateMemberRoom(me._id);
          console.log(`[Socket] Joined corporate member room: ${me._id}`);

          // Join corporate room
          if (me.corporateAccountId) {
            const accId = typeof me.corporateAccountId === 'object' && me.corporateAccountId._id
              ? me.corporateAccountId._id
              : me.corporateAccountId;
            socketService.joinCorporateRoom(accId);
            console.log(`[Socket] Joined corporate room: ${accId}`);
          }
        }
      }, 200);

      // Event listeners
      const removeBookingCreated = socketService.listenForEvent(socketService.events.BOOKING_CREATED, (data) => {
        console.log('[Socket] Booking created:', data);
        toast.info("A new corporate booking was created");
        fetchBookings();
      });

      const removeDriverAssigned = socketService.listenForEvent(socketService.events.DRIVER_ASSIGNED, (data) => {
        console.log('[Socket] Driver assigned:', data);
        const name = data.driver?.name || "A driver";
        toast.success(`Driver ${name} has been assigned to a ride!`);
        fetchBookings();
      });

      const removeStatusChanged = socketService.listenForEvent(socketService.events.BOOKING_STATUS_CHANGED, (data) => {
        console.log('[Socket] Booking status changed:', data);
        toast.info(`Booking status updated: ${data.status}`);
        fetchBookings();
      });

      const removeDriverRemoved = socketService.listenForEvent(socketService.events.DRIVER_REMOVED, (data) => {
        console.log('[Socket] Driver removed:', data);
        toast.warn(`Driver has been unassigned/removed from booking`);
        fetchBookings();
      });

      const removeRideStarted = socketService.listenForEvent(socketService.events.RIDE_STARTED, (data) => {
        console.log('[Socket] Ride started:', data);
        toast.info(`Ride has started!`);
        fetchBookings();
      });

      const removeRideCompleted = socketService.listenForEvent(socketService.events.RIDE_COMPLETED, (data) => {
        console.log('[Socket] Ride completed:', data);
        toast.success(`Ride completed successfully!`);
        fetchBookings();
      });

      const removeRideCancelled = socketService.listenForEvent(socketService.events.RIDE_CANCELLED, (data) => {
        console.log('[Socket] Ride cancelled:', data);
        toast.error(`Ride has been cancelled`);
        fetchBookings();
      });

      return () => {
        console.log("[Socket] Cleaning up corporate booking listeners");
        clearInterval(setupRooms);
        if (typeof removeBookingCreated === 'function') removeBookingCreated();
        if (typeof removeDriverAssigned === 'function') removeDriverAssigned();
        if (typeof removeStatusChanged === 'function') removeStatusChanged();
        if (typeof removeDriverRemoved === 'function') removeDriverRemoved();
        if (typeof removeRideStarted === 'function') removeRideStarted();
        if (typeof removeRideCompleted === 'function') removeRideCompleted();
        if (typeof removeRideCancelled === 'function') removeRideCancelled();
      };
    } catch (err) {
      console.error('[Socket] Failed setting up corporate socket room & listeners:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?._id]);

  // Reset page when filter/search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter]);

  const startCorporateBooking = () => {
    if (me?.role !== "staff" && me?.role !== "admin") {
      toast.error("Only corporate staff and admins can create bookings");
      return;
    }
    sessionStorage.setItem("corporateBookingMode", "1");
    navigate("/", { replace: false });
  };

  // Format helpers
  const formatLocation = (b) => {
    const pickup =
      typeof b.pickupLocation === "object" && b.pickupLocation?.name
        ? `${b.pickupLocation.name}${b.pickupLocation.state ? `, ${b.pickupLocation.state}` : ""}`
        : b.pickupAddress || b.from || "";
    const drop =
      typeof b.dropLocation === "object" && b.dropLocation?.name
        ? `${b.dropLocation.name}${b.dropLocation.state ? `, ${b.dropLocation.state}` : ""}`
        : b.dropAddress || b.to || "";
    return { pickup, drop };
  };

  const getPaymentMethod = (b) => {
    if (!b.paymentMethod) return "company";
    const m = b.paymentMethod.toLowerCase();
    if (m === "cod" || m === "cash") return "cod";
    if (m === "wallet") return "wallet";
    if (m === "company" || m === "corporate") return "company";
    return "online";
  };

  // Filter + search
  const normalizedSearch = searchTerm.trim().toLowerCase().replace(/^#/, "");
  const filtered = bookings.filter((b) => {
    const { pickup, drop } = formatLocation(b);
    const matchesSearch =
      pickup.toLowerCase().includes(normalizedSearch) ||
      drop.toLowerCase().includes(normalizedSearch) ||
      (b.bookingId || "").toLowerCase().includes(normalizedSearch) ||
      (b.cabType?.name || "").toLowerCase().includes(normalizedSearch);
    return matchesSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const startIdx = (currentPage - 1) * itemsPerPage;
  const currentBookings = filtered.slice(startIdx, startIdx + itemsPerPage);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F56753] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Please login again.</div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const s = status || "unknown";
    let classes = "bg-slate-100 text-slate-700 border-slate-200";
    let text = s.replace(/_/g, " ");

    if (s === "completed") {
      classes = "bg-emerald-50 text-emerald-700 border-emerald-200";
      text = "Completed";
    } else if (s === "inProgress") {
      classes = "bg-red-50 text-[#F56753] border-red-200 animate-pulse";
      text = "In Progress";
    } else if (s === "assigned") {
      classes = "bg-amber-50 text-amber-700 border-amber-200";
      text = "Assigned";
    } else if (s === "cancelled") {
      classes = "bg-rose-50 text-rose-700 border-rose-200";
      text = "Cancelled";
    } else if (s === "pending_corporate_approval") {
      classes = "bg-yellow-50 text-yellow-700 border-yellow-200";
      text = "Pending Approval";
    } else if (s === "approved_by_corporate") {
      classes = "bg-teal-50 text-teal-700 border-teal-200";
      text = "Approved";
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${classes}`}>
        {text}
      </span>
    );
  };

  return (
    <div className="bg-slate-50/50 min-h-screen pb-10">

      {/* Header Bar */}
      <div className="max-w-[1180px] mx-auto px-4 mb-6 pt-4">
        <div className="bg-gradient-to-r from-[#F56753] to-[#d94e3a] px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 rounded-2xl shadow-sm">
          <h1 className="text-white text-xl font-bold tracking-tight">Corporate Bookings</h1>
          {(me?.role === "staff" || me?.role === "admin") && (
            <button
              onClick={startCorporateBooking}
              disabled={wallet && wallet.availableCredit < 1000}
              className={`w-full sm:w-auto px-5 py-2 bg-white text-[#F56753] rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-sm transition-all active:scale-[0.98] ${
                wallet && wallet.availableCredit < 1000
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "hover:bg-slate-50"
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" /><path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415" /><path d="M16 5l3 3" />
              </svg>
              {wallet && wallet.availableCredit < 1000 ? "Insufficient Credit" : "Book New Cab"}
            </button>
          )}
        </div>
      </div>

      {/* Low Credit Warning */}
      {wallet && wallet.availableCredit < 10000 && (
        <div className="max-w-[1180px] mx-auto px-4 mb-6">
          <div className={`rounded-2xl p-4 border flex items-start gap-3 shadow-sm ${
            wallet.availableCredit < 5000 ? "bg-red-50 border-red-200 text-red-900" : "bg-orange-50 border-orange-200 text-orange-900"
          }`}>
            <FaExclamationTriangle className={`text-xl flex-shrink-0 mt-0.5 ${wallet.availableCredit < 5000 ? "text-red-600" : "text-orange-600"}`} />
            <div className="flex-1">
              <h3 className="font-extrabold text-sm mb-1">
                {wallet.availableCredit < 5000 ? "Critical: Credit Running Low" : "Low Credit Balance"}
              </h3>
              <p className="text-xs mb-3 opacity-90">
                Available credit: <strong>₹{(wallet.availableCredit || 0).toLocaleString("en-IN")}</strong>. Consider adding funds to avoid service interruption.
              </p>
              <button
                onClick={() => navigate("/corporate/wallet")}
                className={`px-4 py-1.5 rounded-xl text-white text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all active:scale-[0.98] ${
                  wallet.availableCredit < 5000 ? "bg-[#F56753] hover:bg-[#d94e3a]" : "bg-orange-600 hover:bg-orange-700"
                }`}
              >
                <FaWallet size={12} /> View Wallet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3 px-4 max-w-[1180px] mx-auto mb-6">
        <div className="flex items-center gap-2 bg-red-50/50 px-4 py-2.5 rounded-xl flex-1 border border-slate-100 shadow-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-slate-500" viewBox="0 0 16 16" fill="currentColor">
            <path d="m15.7 14.3l-4.2-4.2c-.2-.2-.5-.3-.8-.3c.8-1 1.3-2.4 1.3-3.8c0-3.3-2.7-6-6-6S0 2.7 0 6s2.7 6 6 6c1.4 0 2.8-.5 3.8-1.4c0 .3 0 .6.3.8l4.2 4.2c.2.2.5.3.7.3s.5-.1.7-.3c.4-.3.4-.9 0-1.3zM6 10.5c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5s4.5 2 4.5 4.5s-2 4.5-4.5 4.5z"/>
          </svg>
          <input
            type="text"
            placeholder="Search bookings by ID, location, or car..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-transparent outline-none text-sm w-full text-slate-800 placeholder-slate-400 font-semibold"
          />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="flex-1 sm:flex-initial flex items-center gap-2 bg-red-50/50 px-3.5 py-2.5 rounded-xl border border-slate-100 shadow-sm">
            <FaFilter className="w-3.5 h-3.5 text-slate-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer w-full"
            >
              <option value="all">All Bookings</option>
              <option value="pending_corporate_approval">Pending Approval</option>
              <option value="approved_by_corporate">Approved</option>
              <option value="assigned">Assigned</option>
              <option value="inProgress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <button
            onClick={fetchBookings}
            disabled={listLoading}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all active:scale-[0.98] ${listLoading ? "bg-slate-250 text-slate-400 cursor-not-allowed" : "bg-[#F56753] text-white hover:bg-[#d94e3a]"}`}
          >
            {listLoading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Booking Cards */}
      <div className="max-w-[1180px] mx-auto space-y-4 px-4">
        {listLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F56753]"></div>
          </div>
        ) : currentBookings.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-10 text-center max-w-md mx-auto mt-8">
            <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4 border border-slate-100">
              <FaCar className="text-2xl text-slate-400" />
            </div>
            <h2 className="text-lg font-bold text-slate-850 mb-1">No Bookings Found</h2>
            <p className="text-sm text-slate-500 mb-6">No bookings match your current search queries or filters.</p>
            {(me?.role === "staff" || me?.role === "admin") && (
              <button
                onClick={startCorporateBooking}
                className="w-full bg-[#F56753] hover:bg-[#d94e3a] text-white font-bold py-2.5 rounded-xl text-sm shadow transition-all active:scale-[0.98]"
              >
                Book Your First Corporate Cab
              </button>
            )}
          </div>
        ) : (
          <>
            {currentBookings.map((b) => {
              const { pickup, drop } = formatLocation(b);
              const payMethod = getPaymentMethod(b);
              const bookingRef = b.bookingId || b._id;
              const isMyself = b.bookingFor === "myself" || (
                b.bookingFor !== "staff" && b.bookingFor !== "guest" && b.corporateMemberId && me && (
                  b.corporateMemberId._id === me._id ||
                  b.corporateMemberId.email === me.email ||
                  (b.corporateMemberId.email && b.corporateMemberId.email.toLowerCase() === "nprcop@copcop.com") ||
                  (b.corporateMemberId.mobileNumber && b.corporateMemberId.mobileNumber === "8141809033")
                )
              );
              return (
                <div key={b._id} className="bg-white rounded-2xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow overflow-hidden flex flex-col">
                  {/* Card Header (ID, Date/Time, and Status) */}
                  <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                        #{bookingRef.substring(0, 12).toUpperCase()}
                      </span>
                      <span className="text-[11px] font-bold text-slate-400">
                        {b.pickupDate ? new Date(b.pickupDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        {" • "}
                        {convertTo12HourFormat(b.pickupTime) || b.pickupTime}
                      </span>
                    </div>
                    <div>{getStatusBadge(b.status)}</div>
                  </div>

                  {/* Card Body (Route Timeline and Price) */}
                  <div className="p-4 flex flex-col gap-4 relative">
                    {/* Watermark in background */}
                    <div
                      className="absolute right-4 top-4 hidden md:flex items-center justify-center pointer-events-none select-none"
                      style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: "40px", lineHeight: 1, opacity: 0.05 }}
                    >
                      <span className="text-[#F56753]">Cab</span>
                      <span className="text-orange-500">Zigo</span>
                    </div>

                    {/* Route Timeline */}
                    <div className="flex gap-3 text-left">
                      {/* Timeline Graphic */}
                      <div className="flex flex-col items-center flex-shrink-0 pt-1.5">
                        <div className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-2 ring-emerald-100 flex-shrink-0"></div>
                        <div className="w-[2px] flex-1 bg-gradient-to-b from-emerald-500 via-slate-200 to-rose-500 my-1 min-h-[30px]"></div>
                        <div className="w-3 h-3 rounded-full bg-rose-500 border-2 border-white ring-2 ring-rose-100 flex-shrink-0"></div>
                      </div>

                      {/* Timeline Content */}
                      <div className="flex-1 space-y-3">
                        {/* Pickup */}
                        <div>
                          <p className="font-bold text-slate-800 text-sm leading-snug">{pickup || "—"}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mt-0.5">Pickup Location</p>
                        </div>

                        {/* Via Cities if any */}
                        {(() => {
                          const viaCities = b.viaCities || (b.routeData && b.routeData.viaCities) || (b.viaCitiesString ? b.viaCitiesString.split(',').map(city => city.trim()).filter(city => city.length > 0) : []);
                          return viaCities && viaCities.length > 0 && (
                            <div className="pl-1 py-1 space-y-1">
                              <div className="flex flex-wrap gap-1.5">
                                {viaCities.map((city, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-amber-50 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded border border-amber-200 inline-flex items-center gap-1"
                                  >
                                    📍 {typeof city === "string" ? city : (city?.name || city?.address || "")}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Drop */}
                        <div>
                          <p className="font-bold text-slate-800 text-sm leading-snug">{drop || "—"}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mt-0.5">Drop Location</p>
                        </div>
                      </div>
                    </div>

                    {/* Price and Details link - full width row on mobile */}
                    <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Total Fare</p>
                        <p className="text-2xl font-extrabold text-slate-800 mt-0.5">
                          ₹{(b.totalAmount || b.totalFare || 0).toLocaleString("en-IN")}
                        </p>
                      </div>
                      <Link
                        to={`/booking/${bookingRef}`}
                        className="text-xs font-bold bg-[#F56753] text-white hover:bg-[#d94e3a] px-4 py-2 rounded-xl border border-[#F56753] transition-all active:scale-[0.96]"
                      >
                        👁 View Details
                      </Link>
                    </div>
                  </div>

                  {/* Metadata Chips Grid (Cab, Payment, Distance, Duration) */}
                  <div className="px-4 py-2.5 border-t border-slate-100 flex flex-wrap gap-2 bg-slate-50/20 items-center">
                    {/* Cab type */}
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 shadow-sm">
                      <FaCar className="text-slate-400 text-[10px]" />
                      {b.cabType?.name || "Standard Cab"}
                    </span>

                    {/* Distance */}
                    {b.distance && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 shadow-sm">
                        <FaRoad className="text-slate-400 text-[10px]" />
                        {b.distance} km
                      </span>
                    )}

                    {/* Duration */}
                    {b.duration && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 shadow-sm">
                        <FaHourglassHalf className="text-slate-400 text-[10px]" />
                        {typeof b.duration === "string"
                          ? b.duration
                          : b.duration > 3600
                          ? `${Math.floor(b.duration / 3600)}h ${Math.floor((b.duration % 3600) / 60)}m`
                          : `${b.duration}m`}
                      </span>
                    )}

                    {/* Payment Method */}
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-100 text-[#F56753] rounded-lg text-[11px] font-bold shadow-sm">
                      💳 {getPaymentMethodDisplay(payMethod)}
                    </span>

                    {/* Wallet OTP */}
                    {b.walletOtp && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-100 text-amber-700 rounded-lg text-[11px] font-black shadow-sm animate-pulse">
                        🔐 Wallet OTP: {b.walletOtp}
                      </span>
                    )}
                  </div>

                  {/* Passenger / Staff Section */}
                  {b.corporateMemberId && (
                    <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <img
                          src={b.corporateMemberId.profileImageUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                          alt="Passenger Avatar"
                          className="w-7 h-7 rounded-full object-cover border border-slate-200 shadow-sm"
                          onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png"; }}
                        />
                        <div className="text-left">
                          <p className="text-xs font-bold text-slate-700 leading-tight">
                            {b.corporateMemberId.fullName}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-450 leading-none mt-0.5">
                            {b.corporateMemberId.designation || "Employee"} • {b.corporateMemberId.mobileNumber || b.corporateMemberId.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isMyself ? (
                          <span className="text-[9px] font-extrabold bg-emerald-50 text-emerald-600 border border-emerald-100 px-2 py-0.5 rounded uppercase tracking-wider">
                            Myself
                          </span>
                        ) : b.bookingFor === "guest" || b.isGuest ? (
                          <span className="text-[9px] font-extrabold bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded uppercase tracking-wider">
                            Guest
                          </span>
                        ) : (
                          <span className="text-[9px] font-extrabold bg-red-50 text-[#F56753] border border-red-100 px-2 py-0.5 rounded uppercase tracking-wider">
                            Staff
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Active Actions (Track Live / Driver Coming) */}
                  {(b.status === "assigned" || b.status === "inProgress") && bookingRef && (
                    <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex gap-2">
                      {b.status === "assigned" && (
                        <a
                          href={`/track/${bookingRef}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-95 text-white py-2 rounded-xl text-xs font-bold shadow-sm hover:shadow transition-all text-center"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                          🚚 Driver Coming (Track Driver)
                        </a>
                      )}
                      {b.status === "inProgress" && (
                        <a
                          href={`/track/${bookingRef}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#F56753] to-red-600 hover:opacity-95 text-white py-2 rounded-xl text-xs font-bold shadow-sm hover:shadow transition-all text-center"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          📡 Track Live Ride
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center pb-8">
                <nav className="flex items-center space-x-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 text-xs font-bold rounded-xl border ${currentPage === 1 ? "border-slate-100 text-slate-350 cursor-not-allowed bg-slate-50" : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50"}`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    const start = Math.max(1, currentPage - 4);
                    const page = totalPages <= 10 ? i + 1 : start + i;
                    if (page > totalPages) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 text-xs font-bold rounded-xl flex items-center justify-center transition-all ${currentPage === page ? "bg-[#F56753] text-white shadow-sm shadow-red-200" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                      >
                        {page}
                      </button>
                    );
                  }).filter(Boolean)}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 text-xs font-bold rounded-xl border ${currentPage === totalPages ? "border-slate-100 text-slate-350 cursor-not-allowed bg-slate-50" : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50"}`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CorporateBookings;
